"use client";

import { useTranslation } from "@/lib/i18n/LanguageContext";
import Link from "next/link";

export default function LegalPage() {
  const { t, language } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b" style={{ borderColor: "#e5e5e5" }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link
            href="/"
            className="text-lg font-bold hover:opacity-80 transition-opacity"
            style={{ color: "#1a1a1a" }}
          >
            ← {t("appName")}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-6" style={{ color: "#1a1a1a" }}>
            {language === "fr" ? "Mentions Légales" : "Legal Notice"}
          </h1>

          {language === "fr" ? <FrenchContent /> : <EnglishContent />}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="bg-white border-t mt-12"
        style={{ borderColor: "#e5e5e5" }}
      >
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>
            © {new Date().getFullYear()} SneakGuessr.{" "}
            {language === "fr"
              ? "Tous droits réservés."
              : "All rights reserved."}
          </p>
        </div>
      </footer>
    </div>
  );
}

function FrenchContent() {
  return (
    <div className="space-y-8 text-gray-700">
      {/* Divulgation Amazon */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#f05d01" }}>
          🔗 Divulgation des liens d'affiliation
        </h2>
        <div className="space-y-3 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="font-semibold">
            En tant que Partenaire Amazon, nous réalisons un bénéfice sur les
            achats remplissant les conditions requises.
          </p>
          <p>
            SneakGuessr participe au Programme Partenaires d'Amazon EU, un
            programme d'affiliation conçu pour permettre à des sites de
            percevoir une rémunération grâce à la création de liens vers
            Amazon.fr
          </p>
          <p>
            Lorsque vous cliquez sur un lien Amazon présent sur notre site et
            effectuez un achat, nous pouvons recevoir une petite commission sans
            frais supplémentaires pour vous. Cette commission nous aide à
            maintenir et améliorer SneakGuessr.
          </p>
        </div>
      </section>

      {/* Divulgation StockX */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#f05d01" }}>
          🔗 Partenariat StockX
        </h2>
        <p>
          SneakGuessr peut également contenir des liens d'affiliation vers
          StockX. Si vous effectuez un achat via ces liens, nous pouvons
          recevoir une commission.
        </p>
      </section>

      {/* Éditeur du site */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          📝 Éditeur du site
        </h2>
        <p>SneakGuessr</p>
      </section>

      {/* Hébergement */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          🌐 Hébergement
        </h2>
        <p>
          Ce site est hébergé par Vercel Inc.
          <br />
          440 N Barranca Ave #4133
          <br />
          Covina, CA 91723
          <br />
          États-Unis
        </p>
      </section>

      {/* Propriété intellectuelle */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          © Propriété intellectuelle
        </h2>
        <p>
          Le contenu de ce site (structure, design, textes) est la propriété de
          SneakGuessr, sauf mention contraire.
        </p>
        <p className="mt-2">
          Les images de sneakers affichées appartiennent à leurs propriétaires
          respectifs et sont utilisées à des fins d'illustration uniquement. Les
          marques citées sont la propriété de leurs détenteurs respectifs.
        </p>
      </section>

      {/* Données personnelles */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          🔒 Données personnelles
        </h2>
        <p>
          SneakGuessr ne collecte aucune donnée personnelle identifiable. Nous
          utilisons Vercel Analytics pour des statistiques anonymes de visite.
        </p>
        <p className="mt-2">
          Lorsque vous cliquez sur un lien d'affiliation (Amazon ou StockX),
          vous êtes redirigé vers leur site et leurs propres politiques de
          confidentialité s'appliquent.
        </p>
      </section>

      {/* Cookies */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          🍪 Cookies
        </h2>
        <p>
          SneakGuessr utilise uniquement des cookies techniques nécessaires au
          fonctionnement du jeu (sauvegarde des scores localement).
        </p>
        <p className="mt-2">
          Les sites partenaires (Amazon, StockX) peuvent utiliser leurs propres
          cookies lorsque vous cliquez sur leurs liens.
        </p>
      </section>

      {/* Responsabilité */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          ⚖️ Limitation de responsabilité
        </h2>
        <p>
          Les prix affichés dans le jeu sont fournis à titre indicatif et
          peuvent ne pas refléter les prix réels actuels. SneakGuessr ne peut
          être tenu responsable des variations de prix.
        </p>
        <p className="mt-2">
          Les liens externes vers Amazon et StockX sont fournis pour votre
          commodité. Nous ne sommes pas responsables du contenu de ces sites
          tiers.
        </p>
      </section>

      {/* Dernière mise à jour */}
      <section className="border-t pt-6" style={{ borderColor: "#e5e5e5" }}>
        <p className="text-sm text-gray-500">
          Dernière mise à jour :{" "}
          {new Date().toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Image de fond : Design by Freepik
        </p>
      </section>
    </div>
  );
}

function EnglishContent() {
  return (
    <div className="space-y-8 text-gray-700">
      {/* Amazon Disclosure */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#f05d01" }}>
          🔗 Affiliate Links Disclosure
        </h2>
        <div className="space-y-3 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="font-semibold">
            As an Amazon Associate, we earn from qualifying purchases.
          </p>
          <p>
            SneakGuessr participates in the Amazon EU Associates Programme, an
            affiliate advertising programme designed to provide a means for
            sites to earn advertising fees by advertising and linking to
            Amazon.fr
          </p>
          <p>
            When you click on an Amazon link on our site and make a purchase, we
            may receive a small commission at no extra cost to you. This
            commission helps us maintain and improve SneakGuessr.
          </p>
        </div>
      </section>

      {/* StockX Disclosure */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#f05d01" }}>
          🔗 StockX Partnership
        </h2>
        <p>
          SneakGuessr may also contain affiliate links to StockX. If you make a
          purchase through these links, we may receive a commission.
        </p>
      </section>

      {/* Publisher */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          📝 Publisher
        </h2>
        <p>SneakGuessr</p>
      </section>

      {/* Hosting */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          🌐 Hosting
        </h2>
        <p>
          This site is hosted by Vercel Inc.
          <br />
          440 N Barranca Ave #4133
          <br />
          Covina, CA 91723
          <br />
          United States
        </p>
      </section>

      {/* Intellectual Property */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          © Intellectual Property
        </h2>
        <p>
          The content of this site (structure, design, texts) is the property of
          SneakGuessr, unless otherwise stated.
        </p>
        <p className="mt-2">
          Sneaker images displayed belong to their respective owners and are
          used for illustrative purposes only. Mentioned brands are the property
          of their respective holders.
        </p>
      </section>

      {/* Personal Data */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          🔒 Personal Data
        </h2>
        <p>
          SneakGuessr does not collect any personally identifiable data. We use
          Vercel Analytics for anonymous visit statistics.
        </p>
        <p className="mt-2">
          When you click on an affiliate link (Amazon or StockX), you are
          redirected to their site and their own privacy policies apply.
        </p>
      </section>

      {/* Cookies */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          🍪 Cookies
        </h2>
        <p>
          SneakGuessr only uses technical cookies necessary for the game to
          function (saving scores locally).
        </p>
        <p className="mt-2">
          Partner sites (Amazon, StockX) may use their own cookies when you
          click on their links.
        </p>
      </section>

      {/* Liability */}
      <section>
        <h2 className="text-2xl font-bold mb-4" style={{ color: "#1a1a1a" }}>
          ⚖️ Limitation of Liability
        </h2>
        <p>
          Prices displayed in the game are provided for informational purposes
          and may not reflect actual current prices. SneakGuessr cannot be held
          responsible for price variations.
        </p>
        <p className="mt-2">
          External links to Amazon and StockX are provided for your convenience.
          We are not responsible for the content of these third-party sites.
        </p>
      </section>

      {/* Last Update */}
      <section className="border-t pt-6" style={{ borderColor: "#e5e5e5" }}>
        <p className="text-sm text-gray-500">
          Last updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Background image: Design by Freepik
        </p>
      </section>
    </div>
  );
}
